import { useParams } from "react-router-dom"
import { authClient } from "~/lib/auth-client"
import { api, type RouterOutputs } from "~/trpc/react"

type OrganizationHookLoading = {
	isLoading: true
	organization: undefined
	members: undefined
	isMember: undefined
	invalidate: () => void
}

type OrganizationHookLoaded = {
	isLoading: false
	organization: RouterOutputs["user"]["getOrgs"][number]
	members: RouterOutputs["user"]["getOrgs"][number]["members"]
	isMember: boolean
	invalidate: () => void
}

type OrganizationHookNotOrgRoute = {
	isLoading: false
	organization: undefined
	members: []
	isMember: false
	invalidate: () => void
}

export function useOrganization():
	| OrganizationHookLoading
	| OrganizationHookLoaded
	| OrganizationHookNotOrgRoute {
	const { orgSlug = "" } = useParams<{ orgSlug: string }>()
	const { data: session } = authClient.useSession()
	const { data: orgs } = api.user.getOrgs.useQuery(undefined, {
		enabled: !!orgSlug && !!session
	})
	const utils = api.useUtils()

	const isLoading = !orgs || !session

	if (!orgSlug) {
		return {
			isLoading: false,
			organization: undefined,
			members: [],
			isMember: false,
			invalidate: () => {
				utils.user.getOrgs.invalidate()
			}
		}
	}

	if (isLoading) {
		return {
			isLoading: true,
			organization: undefined,
			members: undefined,
			isMember: undefined,
			invalidate: () => {
				utils.user.getOrgs.invalidate()
			}
		}
	}

	const organization = orgs.find((org) => org.slug === orgSlug)
	if (!organization) {
		return {
			isLoading: false,
			organization: undefined,
			members: [],
			isMember: false,
			invalidate: () => {
				utils.user.getOrgs.invalidate()
			}
		}
	}

	const members = organization.members

	const isMember = members.some((member) => member.userId === session.user.id)

	return {
		isLoading: false,
		organization: organization!,
		members,
		isMember,
		invalidate: () => {
			utils.user.getOrgs.invalidate()
		}
	}
}
